import { Stack, Show, Hide, Flex, Text } from '@chakra-ui/react';
import { isSold, isSellerClaimed } from '@habity/services';
import { BrokerCardProfile } from '~/components/brokers/cards/Profile';
import { AccordionDetailed } from './components/data/AccordionDetailed';
import { FloorPlan } from './components/data/FloorPlan';
import { ProfileTitle } from './components/ProfileTitle';
import { ProfileDescriptionContent } from './components/ProfileDescriptionContent';
import { FeaturesList } from './components/FeaturesList';
import { EmbeddedLinks } from '~/components/ui/EmbeddedLinks';
import { HomeProfileMap } from './components/map/HomeProfileMap';
import { HomePriceDetails } from './components/price/PriceDetails';
import RelatedHomes from './components/related/RelatedHomes';
import { StructuralDataEditor } from './components/StructuralDataEditor';
import { useTranslations } from 'next-intl';
// import { useLoanCalculator } from '~/hooks/providers/useLoanCalculator';
import { useModalWithUrl } from '~/hooks/useModalWithUrl';
import { NestedHome } from '@habity/types';
import { useUser } from '~/hooks/useUser';
import { PageContent } from '~/components/ui/page/content';
import { LegacyBenefits } from './components/LegacyBenefits';
import { useLoanCalculator } from '~/hooks/providers/useLoanCalculator';
import { shouldShowPrice } from '@habity/services';
import { RekaFAQ } from './components/RekaFAQ';
import { usePublicPreviewGate } from '~/hooks/usePublicPreviewGate';

interface HomeProfileContentProps {
  home: NestedHome;
  onOpenViewingModal?: (viewingId?: number) => void;
  relatedHomes: NestedHome[];
  relatedHomesLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

export const HomeProfileContent = ({
  home,
  onOpenViewingModal,
  relatedHomes,
  relatedHomesLoading,
  hasMore,
  loadMore,
}: HomeProfileContentProps) => {
  const t = useTranslations('HomeProfile');
  const { calculatorElement, loadFailed } = useLoanCalculator(home);
  const { handleOpenWithUrlUpdate } = useModalWithUrl(home);
  const { status, responsible_broker, additional_contact, images } = home || {};
  const { show_broker } = status || {};
  const show_price = shouldShowPrice(home);
  const { user } = useUser();

  const isReka = status.name === "seller_claimed";

  const { sentinelRef } = usePublicPreviewGate(home);

  return (
    <PageContent containerProps={{ py: { mobile: 4, desktop: 10 } }}>
      <Stack
        direction={{ mobile: 'column-reverse', desktop: 'row' }}
        align="flex-start"
        spacing={{ mobile: '8', desktop: '16' }}
      >
        <Stack spacing="4" flex="3" pt={{ mobile: 4, desktop: 0 }}>
          {/* Mobile: Price */}
          {show_price && (
            <Show below="desktop">
              <HomePriceDetails
                home={home}
                onOpenViewingModal={onOpenViewingModal}
              />
            </Show>
          )}

          {/* Title */}
          <ProfileTitle home={home} />

          {/* Legacy benefits */}
          <LegacyBenefits benefits={home?.benefits} />

          {/* Description */}
          <ProfileDescriptionContent home={home} />

          {/* Embedded links (Locka, Vimeo, YouTube) */}
          <EmbeddedLinks entity={home} />

          {/* Floor plan */}
          <FloorPlan
            images={images?.filter((i) => i.is_floor_plan)}
            handleOpenGallery={() =>
              handleOpenWithUrlUpdate({ modalType: 'floorPlan', open: true })
            }
          />

          {/* Scroll sentinel for public preview gate */}
          <div ref={sentinelRef} />

          {/* Structural data - inline editable */}
          <StructuralDataEditor home={home} user={user} />

          {/* Best things about the home */}
          <FeaturesList home={home} />

          {/* Home full details */}
          {!isSold(home) &&
            !isSellerClaimed(home) &&
            home.status?.show_address && (
              <AccordionDetailed
                home={home}
                handleOpen={handleOpenWithUrlUpdate}
              />
            )}
        </Stack>

        {/* Desktop 2nd column */}
        <Hide below="desktop">
          <Flex direction="column" align="left" flex="2" pl="20">
            <Stack spacing="20">
              {/* Price */}
              {show_price && (
                <HomePriceDetails
                  home={home}
                  onOpenViewingModal={onOpenViewingModal}
                />
              )}

              {/* Broker cards on desktop are in the 2nd column */}
              {show_broker && responsible_broker && (
                <BrokerCardProfile
                  home={home}
                  homeRelation="responsibleBroker"
                  broker={responsible_broker}
                  customTitle={t('Brokers.responsibleBroker')}
                  buttonVariant="homes"
                  showButtons
                />
              )}

              {show_broker &&
                additional_contact &&
                additional_contact.id !== responsible_broker?.id && (
                  <BrokerCardProfile
                    home={home}
                    homeRelation="additionalContact"
                    broker={additional_contact}
                    buttonVariant="homes"
                    customTitle={t('Brokers.additionalContact')}
                  />
                )}
              {isReka && <RekaFAQ />}
            </Stack>
          </Flex>
        </Hide>
      </Stack>

      {/* Map, surroundings, nearby */}
      <Stack
        mt={{ mobile: '4', desktop: '20' }}
        py={{ mobile: '4', desktop: '20' }}
        borderTopWidth={{ mobile: '0', desktop: '1px' }}
        borderBottomWidth={{ mobile: '0', desktop: '1px' }}
        borderColor="blackAlpha.300"
      >
        <HomeProfileMap home={home} />
      </Stack>

      {/* Broker cards on mobile. In the same column as other details */}
      <Show below="desktop">
        <Stack
          gap={20}
          py="10"
          borderBottomWidth="1px"
          borderBottomColor="blackAlpha.200"
        >
          {show_broker && responsible_broker && (
            <BrokerCardProfile
              home={home}
              homeRelation="responsibleBroker"
              broker={responsible_broker}
              customTitle={t('Brokers.responsibleBroker')}
              buttonVariant="homes"
              showButtons
            />
          )}
          {show_broker &&
            additional_contact &&
            additional_contact.id !== responsible_broker?.id && (
              <BrokerCardProfile
                home={home}
                homeRelation="additionalContact"
                broker={additional_contact}
                buttonVariant="homes"
                customTitle={t('Brokers.additionalContact')}
              />
            )}
        </Stack>
      </Show>

      {/* Loan Calculator - only show if loading didn't fail */}
      {!loadFailed && calculatorElement && (
        <Stack
          py="6"
          mt="10"
          borderBottomWidth="1px"
          borderColor="blackAlpha.200"
        >
          <Text
            size={{ mobile: 'sm', desktop: 'md' }}
            fontWeight="light"
            mb="2"
          >
            {t('Homes.loanCalculator')}
          </Text>
          {calculatorElement}
        </Stack>
      )}

      {/* Related homes */}
      <Stack py="2" mt={{ mobile: '6', desktop: '10' }}>
        <RelatedHomes
          relatedHomes={relatedHomes}
          relatedHomesLoading={relatedHomesLoading}
          hasMore={hasMore}
          loadMore={loadMore}
          user={user}
        />
      </Stack>
    </PageContent>
  );
};
